"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import {
  optimizeImageBlob,
  saveProfilePhoto,
  getProfilePhoto,
  deleteProfilePhoto,
  type PhotoView,
  type StoredPhoto,
} from "@/lib/profilePhotos";

/* ── Types ─────────────────────────────────────────────────── */
interface ProfileData {
  height: number | null;
  heightUnit: "cm" | "ft";
  weight: number | null;
  weightUnit: "kg" | "lb";
  bodyType: string;
  topSize: string;
  bottomSize: string;
  fitPreference: string;
  skinTone: string;
  preferredColors: string[];
  fitNotes: string;
}

const DEFAULT_PROFILE: ProfileData = {
  height: null,
  heightUnit: "cm",
  weight: null,
  weightUnit: "kg",
  bodyType: "",
  topSize: "",
  bottomSize: "",
  fitPreference: "Regular",
  skinTone: "",
  preferredColors: [],
  fitNotes: "",
};

const BODY_TYPES = [
  { id: "Slim", icon: "M12 4c-2 4-2 10 0 16 2-6 2-12 0-16z" },
  { id: "Athletic", icon: "M12 4c-3 4-3 10 0 16 3-6 3-12 0-16z" },
  { id: "Regular", icon: "M12 4c-2.5 4-2.5 10 0 16 2.5-6 2.5-12 0-16z" },
  { id: "Curvy", icon: "M12 4c-3.5 4-3.5 10 0 16 3.5-6 3.5-12 0-16z" },
  { id: "Plus", icon: "M12 4c-4 4-4 10 0 16 4-6 4-12 0-16z" },
];

const SKIN_TONES = [
  "#f8d5c2",
  "#f0c8a8",
  "#e0b088",
  "#c89870",
  "#b08058",
  "#986848",
  "#805038",
  "#604028",
];

const COLOR_PALETTE = [
  { name: "Black", hex: "#11110f" },
  { name: "White", hex: "#ffffff" },
  { name: "Navy", hex: "#1a1a3e" },
  { name: "Grey", hex: "#7a7a7a" },
  { name: "Cream", hex: "#f5f0e8" },
  { name: "Olive", hex: "#6b6b3a" },
  { name: "Burgundy", hex: "#5e1e2e" },
  { name: "Forest", hex: "#2d4a2b" },
  { name: "Sky", hex: "#7cb8d8" },
  { name: "Sage", hex: "#9caf88" },
  { name: "Tan", hex: "#d4b896" },
  { name: "Coral", hex: "#e8826b" },
  { name: "Mustard", hex: "#d4a017" },
  { name: "Lavender", hex: "#b0a8c0" },
  { name: "Rose", hex: "#d4989e" },
  { name: "Teal", hex: "#2d8a8a" },
  { name: "Chocolate", hex: "#4a3020" },
  { name: "Sand", hex: "#c2b280" },
];

const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const STORAGE_KEY = "dressingroom-profile";

/* ── Page Component ────────────────────────────────────────── */
export default function ProfilePage() {
  const [profile, setProfile] = useState<ProfileData>(DEFAULT_PROFILE);
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState("");
  const [photos, setPhotos] = useState<Record<PhotoView, StoredPhoto | null>>({
    front: null,
    side: null,
    back: null,
  });
  const [activeTab, setActiveTab] = useState<PhotoView>("front");
  const [dragOver, setDragOver] = useState<PhotoView | null>(null);
  const [shopper, setShopper] = useState<{ id: string; name: string; email: string } | null>(null);
  const [shopperLoaded, setShopperLoaded] = useState(false);

  // Check if shopper is logged in
  useEffect(() => {
    fetch("/api/shopper/me")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.shopper) setShopper(data.shopper);
        setShopperLoaded(true);
      })
      .catch(() => setShopperLoaded(true));
  }, []);

  // Load profile from localStorage or server
  useEffect(() => {
    async function loadProfile() {
      // If shopper is logged in, load from server
      if (shopper) {
        try {
          const res = await fetch("/api/shopper/profile");
          if (res.ok) {
            const data = await res.json();
            const p = data.profile;
            if (p) {
              setProfile({
                height: p.height_cm ? Number(p.height_cm) : null,
                heightUnit: p.height_unit || "cm",
                weight: p.weight_kg ? Number(p.weight_kg) : null,
                weightUnit: p.weight_unit || "kg",
                bodyType: p.body_type || "",
                topSize: p.usual_top_size || "",
                bottomSize: p.usual_bottom_size || "",
                fitPreference: p.fit_preference || "Regular",
                skinTone: p.skin_tone || "",
                preferredColors: Array.isArray(p.preferred_colors) ? p.preferred_colors : [],
                fitNotes: p.fit_notes || "",
              });
              setLoaded(true);
              return;
            }
          }
        } catch {
          // fall through to localStorage
        }
      }
      // Fallback to localStorage
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          setProfile({ ...DEFAULT_PROFILE, ...JSON.parse(saved) });
        }
      } catch {
        // ignore
      }
      setLoaded(true);
    }
    loadProfile();
  }, [shopper]);

  // Load photos from IndexedDB
  useEffect(() => {
    (async () => {
      const [front, side, back] = await Promise.all([
        getProfilePhoto("front"),
        getProfilePhoto("side"),
        getProfilePhoto("back"),
      ]);
      setPhotos({ front, side, back });
    })();
  }, []);

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      Object.values(photos).forEach((p) => {
        if (p) URL.revokeObjectURL(p.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  }, []);

  function updateProfile<K extends keyof ProfileData>(key: K, value: ProfileData[K]) {
    setProfile((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
      if (shopper) {
        const res = await fetch("/api/shopper/profile", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            height_cm: profile.height,
            weight_kg: profile.weight,
            height_unit: profile.heightUnit,
            weight_unit: profile.weightUnit,
            body_type: profile.bodyType,
            usual_top_size: profile.topSize,
            usual_bottom_size: profile.bottomSize,
            fit_preference: profile.fitPreference,
            skin_tone: profile.skinTone,
            preferred_colors: profile.preferredColors,
            fit_notes: profile.fitNotes,
          }),
        });
        if (!res.ok) throw new Error("Server save failed");
      }
      showToast("Profile saved");
    } catch {
      showToast("Could not save profile");
    }
  }

  function handleReset() {
    setProfile(DEFAULT_PROFILE);
    localStorage.removeItem(STORAGE_KEY);
    showToast("Profile cleared");
  }

  function toggleColor(hex: string) {
    setProfile((prev) => ({
      ...prev,
      preferredColors: prev.preferredColors.includes(hex)
        ? prev.preferredColors.filter((c) => c !== hex)
        : [...prev.preferredColors, hex],
    }));
  }

  async function handlePhotoUpload(view: PhotoView, file: File) {
    try {
      const optimized = await optimizeImageBlob(file);
      await saveProfilePhoto(view, optimized, file.name);
      const stored = await getProfilePhoto(view);
      if (stored) {
        setPhotos((prev) => {
          if (prev[view]) URL.revokeObjectURL(prev[view]!.url);
          return { ...prev, [view]: stored };
        });
      }
    } catch {
      showToast("Photo upload failed");
    }
  }

  async function handlePhotoRemove(view: PhotoView) {
    await deleteProfilePhoto(view);
    setPhotos((prev) => {
      if (prev[view]) URL.revokeObjectURL(prev[view]!.url);
      return { ...prev, [view]: null };
    });
  }

  function onFileInput(view: PhotoView, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handlePhotoUpload(view, file);
  }

  function onDrop(view: PhotoView, e: React.DragEvent) {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) handlePhotoUpload(view, file);
  }

  if (!loaded || !shopperLoaded) return null;

  return (
    <>
      <header className="site-header">
        <Link href="/" className="site-header__brand">DressingRoom</Link>
        <nav className="site-header__nav">
          <Link href="/profile" className="site-header__link site-header__link--active">Profile</Link>
          <Link href="/store" className="site-header__link">Stores</Link>
          <Link href="/catalog" className="site-header__link">Catalog</Link>
          <Link href="/saved" className="site-header__link">Saved</Link>
          {shopperLoaded && (
            <div className="site-header__auth">
              {shopper ? (
                <>
                  <span className="site-header__user">{shopper.name}</span>
                  <button
                    className="site-header__link site-header__link--btn"
                    onClick={async () => {
                      await fetch("/api/shopper/logout", { method: "POST" });
                      setShopper(null);
                      window.location.reload();
                    }}
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link href="/login" className="site-header__link">Sign In</Link>
                  <Link href="/signup" className="site-header__link site-header__link--cta">Sign Up</Link>
                </>
              )}
            </div>
          )}
        </nav>
      </header>

      <div className="app-shell">
        {/* ── Left Panel (nav / context) ──────────────────── */}
        <aside className="app-shell__left">
          <div className="filter-group">
            <h3 className="filter-group__title">Account</h3>
            <div className="filter-list">
              <span className="filter-item filter-item--active">Profile Setup</span>
              <Link href="/catalog" className="filter-item">Browse Catalog</Link>
              <Link href="/saved" className="filter-item">Saved Looks</Link>
            </div>
          </div>
          <div className="filter-group">
            <h3 className="filter-group__title">About</h3>
            <p style={{ fontSize: "var(--fs-small)", color: "var(--text-muted)", lineHeight: 1.6 }}>
              DressingRoom uses AI to simulate how garments fit your unique body —
              built for Caribbean e-commerce.
            </p>
          </div>
        </aside>

        {/* ── Main Panel ───────────────────────────────────── */}
        <main className="app-shell__main">
          <h1 className="page-title">Your Profile</h1>
          <p className="page-subtitle">
            Set your measurements and preferences for accurate try-on results.
          </p>

          {/* Measurements */}
          <div className="field-group">
            <label className="field-label">Height</label>
            <div className="compound-input">
              <input
                type="number"
                min="0"
                placeholder="175"
                value={profile.height ?? ""}
                onChange={(e) => updateProfile("height", e.target.value ? Number(e.target.value) : null)}
              />
              <select
                value={profile.heightUnit}
                onChange={(e) => updateProfile("heightUnit", e.target.value as "cm" | "ft")}
              >
                <option value="cm">cm</option>
                <option value="ft">ft</option>
              </select>
            </div>
          </div>

          <div className="field-group">
            <label className="field-label">Weight</label>
            <div className="compound-input">
              <input
                type="number"
                min="0"
                placeholder="70"
                value={profile.weight ?? ""}
                onChange={(e) => updateProfile("weight", e.target.value ? Number(e.target.value) : null)}
              />
              <select
                value={profile.weightUnit}
                onChange={(e) => updateProfile("weightUnit", e.target.value as "kg" | "lb")}
              >
                <option value="kg">kg</option>
                <option value="lb">lb</option>
              </select>
            </div>
          </div>

          {/* Body Type */}
          <div className="field-group">
            <label className="field-label">Body Type</label>
            <div className="body-type-grid">
              {BODY_TYPES.map((bt) => (
                <div
                  key={bt.id}
                  className={`body-type-card ${profile.bodyType === bt.id ? "body-type-card--active" : ""}`}
                  onClick={() => updateProfile("bodyType", bt.id)}
                >
                  <div className="body-type-card__icon">
                    <svg viewBox="0 0 24 24">
                      <path d={bt.icon} />
                    </svg>
                  </div>
                  <span className="body-type-card__label">{bt.id}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Usual Sizes */}
          <div className="field-group">
            <label className="field-label">Usual Sizes</label>
            <div className="split-selects">
              <div className="select-box">
                <select
                  value={profile.topSize}
                  onChange={(e) => updateProfile("topSize", e.target.value)}
                >
                  <option value="">Top size</option>
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="select-box">
                <select
                  value={profile.bottomSize}
                  onChange={(e) => updateProfile("bottomSize", e.target.value)}
                >
                  <option value="">Bottom size</option>
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Fit Preference */}
          <div className="field-group">
            <label className="field-label">Fit Preference</label>
            <div className="fit-grid">
              {["Slim", "Regular", "Oversized"].map((f) => (
                <div
                  key={f}
                  className={`fit-option ${profile.fitPreference === f ? "fit-option--active" : ""}`}
                  onClick={() => updateProfile("fitPreference", f)}
                >
                  {f}
                </div>
              ))}
            </div>
          </div>

          {/* Skin Tone */}
          <div className="field-group">
            <label className="field-label">Skin Tone</label>
            <div className="skin-tone-row">
              {SKIN_TONES.map((hex) => (
                <div
                  key={hex}
                  className={`skin-tone-swatch ${profile.skinTone === hex ? "skin-tone-swatch--active" : ""}`}
                  style={{ background: hex }}
                  onClick={() => updateProfile("skinTone", hex)}
                />
              ))}
            </div>
          </div>

          {/* Preferred Colors */}
          <div className="field-group">
            <label className="field-label">Preferred Colors</label>
            <div className="color-palette">
              {COLOR_PALETTE.map((c) => (
                <div
                  key={c.hex}
                  className={`color-swatch ${profile.preferredColors.includes(c.hex) ? "color-swatch--active" : ""}`}
                  style={{ background: c.hex }}
                  title={c.name}
                  onClick={() => toggleColor(c.hex)}
                />
              ))}
            </div>
          </div>

          {/* Photo Uploads */}
          <div className="field-group">
            <label className="field-label">Reference Photos</label>
            <div className="upload-grid">
              {(["front", "side", "back"] as PhotoView[]).map((view) => (
                <div
                  key={view}
                  className={`upload-card ${photos[view] ? "upload-card--has-image" : ""} ${dragOver === view ? "upload-card--dragover" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(view); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={(e) => onDrop(view, e)}
                  onClick={() => {
                    if (!photos[view]) {
                      document.getElementById(`upload-${view}`)?.click();
                    }
                  }}
                >
                  {photos[view] ? (
                    <>
                      <img className="upload-card__preview" src={photos[view]!.url} alt={view} />
                      <button
                        className="upload-card__remove"
                        onClick={(e) => { e.stopPropagation(); handlePhotoRemove(view); }}
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <>
                      <svg className="upload-card__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 5v10M7 10l5-5 5 5M5 19h14" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="upload-card__label">{view}</span>
                    </>
                  )}
                  <input
                    id={`upload-${view}`}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => onFileInput(view, e)}
                  />
                </div>
              ))}
            </div>
            <p style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)", marginTop: "var(--sp-2)" }}>
              For best results, use full-body photos in fitted clothing against a plain background.
            </p>
          </div>

          {/* Fit Notes */}
          <div className="field-group">
            <label className="field-label">Fit Notes</label>
            <textarea
              className="textarea"
              maxLength={300}
              placeholder="e.g. broad shoulders, long arms, prefer longer tops…"
              value={profile.fitNotes}
              onChange={(e) => updateProfile("fitNotes", e.target.value)}
            />
            <div style={{ fontSize: "var(--fs-micro)", color: "var(--text-muted)", marginTop: "2px", textAlign: "right" }}>
              {profile.fitNotes.length} / 300
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: "var(--sp-3)", marginBottom: "var(--sp-8)" }}>
            <button className="button button--primary" onClick={handleSave}>
              Save Profile
            </button>
            <button className="button button--secondary" onClick={handleReset}>
              Reset
            </button>
          </div>
        </main>

        {/* ── Right Panel (Preview) ────────────────────────── */}
        <aside className="app-shell__right">
          <div className="right-panel">
            <div className="right-panel__section">
              <h3 className="right-panel__title">Profile Preview</h3>

              <div className="profile-preview__tabs">
                {(["front", "side", "back"] as PhotoView[]).map((v) => (
                  <div
                    key={v}
                    className={`profile-preview__tab ${activeTab === v ? "profile-preview__tab--active" : ""}`}
                    onClick={() => setActiveTab(v)}
                  >
                    {v}
                  </div>
                ))}
              </div>

              <div className="profile-preview__image">
                {photos[activeTab] ? (
                  <img src={photos[activeTab]!.url} alt={activeTab} />
                ) : (
                  <div className="profile-preview__empty">
                    No {activeTab} photo
                  </div>
                )}
              </div>

              <div className="profile-summary">
                {profile.height && (
                  <div className="profile-summary__row">
                    <span className="profile-summary__label">Height</span>
                    <span className="profile-summary__value">{profile.height} {profile.heightUnit}</span>
                  </div>
                )}
                {profile.weight && (
                  <div className="profile-summary__row">
                    <span className="profile-summary__label">Weight</span>
                    <span className="profile-summary__value">{profile.weight} {profile.weightUnit}</span>
                  </div>
                )}
                {profile.bodyType && (
                  <div className="profile-summary__row">
                    <span className="profile-summary__label">Body Type</span>
                    <span className="profile-summary__value">{profile.bodyType}</span>
                  </div>
                )}
                {profile.topSize && (
                  <div className="profile-summary__row">
                    <span className="profile-summary__label">Top Size</span>
                    <span className="profile-summary__value">{profile.topSize}</span>
                  </div>
                )}
                {profile.bottomSize && (
                  <div className="profile-summary__row">
                    <span className="profile-summary__label">Bottom Size</span>
                    <span className="profile-summary__value">{profile.bottomSize}</span>
                  </div>
                )}
                <div className="profile-summary__row">
                  <span className="profile-summary__label">Fit</span>
                  <span className="profile-summary__value">{profile.fitPreference}</span>
                </div>
                {profile.skinTone && (
                  <div className="profile-summary__row">
                    <span className="profile-summary__label">Skin Tone</span>
                    <span className="profile-summary__value" style={{ display: "inline-block", width: "16px", height: "16px", borderRadius: "50%", background: profile.skinTone, border: "1px solid var(--border)" }} />
                  </div>
                )}
                {profile.preferredColors.length > 0 && (
                  <div className="profile-summary__row">
                    <span className="profile-summary__label">Colors</span>
                    <span className="profile-summary__value">
                      <div style={{ display: "flex", gap: "3px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {profile.preferredColors.map((hex) => (
                          <span key={hex} style={{ display: "inline-block", width: "14px", height: "14px", borderRadius: "50%", background: hex, border: "1px solid var(--border)" }} />
                        ))}
                      </div>
                    </span>
                  </div>
                )}
                {profile.fitNotes && (
                  <div className="profile-summary__row">
                    <span className="profile-summary__label">Notes</span>
                    <span className="profile-summary__value" style={{ maxWidth: "60%", textAlign: "right", fontSize: "var(--fs-micro)" }}>
                      {profile.fitNotes}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>

      {toast && <div className="toast">{toast}</div>}

      {/* Mobile nav */}
      <nav className="mobile-nav">
        <Link href="/profile" className="mobile-nav__btn mobile-nav__btn--active">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="10" cy="7" r="3" /><path d="M4 17c0-3 3-5 6-5s6 2 6 5" />
          </svg>
          Profile
        </Link>
        <Link href="/catalog" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="6" height="6" /><rect x="11" y="3" width="6" height="6" /><rect x="3" y="11" width="6" height="6" /><rect x="11" y="11" width="6" height="6" />
          </svg>
          Catalog
        </Link>
        <Link href="/saved" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M5 3h10v14l-5-3-5 3V3z" />
          </svg>
          Saved
        </Link>
      </nav>
    </>
  );
}
